/**
 * Fractional indexing for drag & drop ordering.
 *
 * Positions are strings ordered lexicographically, and a key can always be
 * produced strictly between two existing ones. Moving an item therefore writes
 * exactly one row, which matters because two people reordering the same list
 * offline must not have to renumber every sibling and collide on every one of
 * them (SPEC §7, last-write-wins per field).
 *
 * A key is an integer part followed by an optional fractional part:
 *
 *     a0      integer 0
 *     a0V     integer 0, fraction .V
 *     b00     integer 62
 *     Zz      integer -1
 *
 * The integer's first character encodes how many digits follow, so appending to
 * the end of a list increments the integer rather than lengthening a fraction.
 * A purely fractional scheme grows one character per append, which is the single
 * most common operation in this app.
 *
 * Invariant: the fractional part never ends in "0". Trailing zeros are redundant
 * — "a0V" and "a0V0" denote the same number — and permitting both would break
 * the assumption that lexicographic order equals numeric order.
 */

const DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const SMALLEST_DIGIT = DIGITS[0]!;
const LARGEST_DIGIT = DIGITS[DIGITS.length - 1]!;

/** Integer zero. The key handed out when a list is empty. */
const ZERO = "a0";

/** Nothing sorts before this; reaching it means the list has been prepended to ~62^26 times. */
const SMALLEST_INTEGER = "A" + SMALLEST_DIGIT.repeat(26);

export class OrderKeyError extends Error {
  override name = "OrderKeyError";
}

/**
 * How many characters the integer part occupies, encoded in its first one.
 * 'a'..'z' count up for non-negative integers, 'Z'..'A' count up for negative.
 */
function integerLength(head: string): number {
  if (head >= "a" && head <= "z") return head.charCodeAt(0) - "a".charCodeAt(0) + 2;
  if (head >= "A" && head <= "Z") return "Z".charCodeAt(0) - head.charCodeAt(0) + 2;
  throw new OrderKeyError(`invalid order key head: ${JSON.stringify(head)}`);
}

function assertValidInteger(int: string): void {
  if (int.length !== integerLength(int[0]!)) {
    throw new OrderKeyError(`invalid integer part: ${JSON.stringify(int)}`);
  }
}

function integerPart(key: string): string {
  const length = integerLength(key[0]!);
  if (length > key.length) {
    throw new OrderKeyError(`order key is shorter than its integer part: ${key}`);
  }
  return key.slice(0, length);
}

function hasOnlyDigits(value: string): boolean {
  for (const char of value) if (!DIGITS.includes(char)) return false;
  return true;
}

export function isValidOrderKey(key: string): boolean {
  if (key.length === 0 || key === SMALLEST_INTEGER) return false;

  let int: string;
  try {
    int = integerPart(key);
  } catch {
    return false;
  }

  if (!hasOnlyDigits(int.slice(1))) return false;

  const fraction = key.slice(int.length);
  if (!hasOnlyDigits(fraction)) return false;
  if (fraction.endsWith(SMALLEST_DIGIT)) return false;

  return true;
}

function assertValidKey(key: string | null, label: string): void {
  if (key === null) return;
  if (!isValidOrderKey(key)) {
    throw new OrderKeyError(`${label} is not a valid order key: ${JSON.stringify(key)}`);
  }
}

/** The next integer up, or null when the encoding is exhausted. */
function incrementInteger(int: string): string | null {
  assertValidInteger(int);

  const head = int[0]!;
  const digits = int.slice(1).split("");

  let carry = true;
  for (let i = digits.length - 1; carry && i >= 0; i--) {
    const next = DIGITS.indexOf(digits[i]!) + 1;
    if (next === DIGITS.length) {
      digits[i] = SMALLEST_DIGIT;
    } else {
      digits[i] = DIGITS[next]!;
      carry = false;
    }
  }

  if (!carry) return head + digits.join("");

  // Overflowed the current width, so the head moves to the next one.
  if (head === "Z") return "a" + SMALLEST_DIGIT;
  if (head === "z") return null;

  const nextHead = String.fromCharCode(head.charCodeAt(0) + 1);
  if (nextHead > "a") digits.push(SMALLEST_DIGIT);
  else digits.pop();

  return nextHead + digits.join("");
}

/** The next integer down, or null when the encoding is exhausted. */
function decrementInteger(int: string): string | null {
  assertValidInteger(int);

  const head = int[0]!;
  const digits = int.slice(1).split("");

  let borrow = true;
  for (let i = digits.length - 1; borrow && i >= 0; i--) {
    const next = DIGITS.indexOf(digits[i]!) - 1;
    if (next === -1) {
      digits[i] = LARGEST_DIGIT;
    } else {
      digits[i] = DIGITS[next]!;
      borrow = false;
    }
  }

  if (!borrow) return head + digits.join("");

  if (head === "a") return "Z" + LARGEST_DIGIT;
  if (head === "A") return null;

  const nextHead = String.fromCharCode(head.charCodeAt(0) - 1);
  if (nextHead < "Z") digits.push(LARGEST_DIGIT);
  else digits.pop();

  return nextHead + digits.join("");
}

/**
 * A fraction strictly between `lower` and `upper`, both read as digits after an
 * implied decimal point. `lower === ""` means 0; `upper === null` means 1.
 */
function midpoint(lower: string, upper: string | null): string {
  if (upper !== null && lower >= upper) {
    throw new OrderKeyError(`cannot place a fraction between ${lower} and ${upper}`);
  }

  if (upper !== null) {
    let shared = 0;
    while ((lower[shared] ?? SMALLEST_DIGIT) === upper[shared]) shared++;
    if (shared > 0) {
      return upper.slice(0, shared) + midpoint(lower.slice(shared), upper.slice(shared));
    }
  }

  const lowerDigit = lower === "" ? 0 : DIGITS.indexOf(lower[0]!);
  const upperDigit = upper === null ? DIGITS.length : DIGITS.indexOf(upper[0]!);

  if (upperDigit - lowerDigit > 1) {
    return DIGITS[Math.round(0.5 * (lowerDigit + upperDigit))]!;
  }

  // Adjacent digits, so the answer must be longer than both inputs.
  if (upper !== null && upper.length > 1) return upper.slice(0, 1);

  return DIGITS[lowerDigit]! + midpoint(lower.slice(1), null);
}

/**
 * Produce a key ordered strictly between `before` and `after`.
 * Pass null for either side to prepend to the start or append to the end.
 */
export function orderKeyBetween(before: string | null, after: string | null): string {
  assertValidKey(before, "before");
  assertValidKey(after, "after");

  if (before !== null && after !== null && before >= after) {
    throw new OrderKeyError(
      `before must sort ahead of after, got ${before} and ${after}`,
    );
  }

  if (before === null) {
    if (after === null) return ZERO;

    const afterInt = integerPart(after);
    const afterFraction = after.slice(afterInt.length);

    if (afterInt === SMALLEST_INTEGER) return afterInt + midpoint("", afterFraction);
    if (afterInt < after) return afterInt;

    const decremented = decrementInteger(afterInt);
    if (decremented === null) {
      throw new OrderKeyError("ran out of room before the first key");
    }
    return decremented;
  }

  if (after === null) {
    const beforeInt = integerPart(before);
    const beforeFraction = before.slice(beforeInt.length);

    const incremented = incrementInteger(beforeInt);
    return incremented === null
      ? beforeInt + midpoint(beforeFraction, null)
      : incremented;
  }

  const beforeInt = integerPart(before);
  const beforeFraction = before.slice(beforeInt.length);
  const afterInt = integerPart(after);
  const afterFraction = after.slice(afterInt.length);

  if (beforeInt === afterInt) {
    return beforeInt + midpoint(beforeFraction, afterFraction);
  }

  const incremented = incrementInteger(beforeInt);
  if (incremented === null) {
    throw new OrderKeyError("ran out of room between the two keys");
  }
  if (incremented < after) return incremented;

  return beforeInt + midpoint(beforeFraction, null);
}

/** `count` keys in ascending order, all strictly between `before` and `after`. */
export function orderKeysBetween(
  before: string | null,
  after: string | null,
  count: number,
): string[] {
  if (!Number.isInteger(count) || count < 0) {
    throw new OrderKeyError(`count must be a non-negative integer, got ${count}`);
  }
  if (count === 0) return [];
  if (count === 1) return [orderKeyBetween(before, after)];

  // With an open end, walking outward keeps every key short. Bisecting a bounded
  // gap keeps the keys balanced instead of piling depth onto one side.
  if (after === null) {
    let cursor = orderKeyBetween(before, null);
    const keys = [cursor];
    while (keys.length < count) {
      cursor = orderKeyBetween(cursor, null);
      keys.push(cursor);
    }
    return keys;
  }

  if (before === null) {
    let cursor = orderKeyBetween(null, after);
    const keys = [cursor];
    while (keys.length < count) {
      cursor = orderKeyBetween(null, cursor);
      keys.unshift(cursor);
    }
    return keys;
  }

  const middle = Math.floor((count - 1) / 2);
  const pivot = orderKeyBetween(before, after);

  return [
    ...orderKeysBetween(before, pivot, middle),
    pivot,
    ...orderKeysBetween(pivot, after, count - middle - 1),
  ];
}

/** The key for an item appended after every existing one. */
export function orderKeyAfterLast(keys: readonly string[]): string {
  const last = keys.length === 0 ? null : keys.reduce((a, b) => (a > b ? a : b));
  return orderKeyBetween(last, null);
}

/** The key for an item placed before every existing one. */
export function orderKeyBeforeFirst(keys: readonly string[]): string {
  const first = keys.length === 0 ? null : keys.reduce((a, b) => (a < b ? a : b));
  return orderKeyBetween(null, first);
}
